// Image carousel — a sliding track inside any `[data-image-carousel]`
// (the Images section's "Carousel" type). Hand-rolled to match the rest
// of the theme (no slider library); the hero has its own crossfade
// slider, but a carousel needs a translating track + responsive
// slides-per-view, so it lives here.
//
// Widths and slides-per-view come from CSS (_images.scss); the JS just
// steps the track by each slide's offset and clamps so the last slide
// sits flush against the right edge. Arrows disable at the ends; dots
// past the final reachable position are disabled on resize.
//
// Autoplay: opt-in via `data-carousel-autoplay`, speed in seconds via
// `data-carousel-speed` (fallback 6). It pauses on hover/focus and never
// starts for `prefers-reduced-motion: reduce` visitors.

const DEFAULT_SPEED_SECONDS = 6;

function buildCarousel(root) {
    const track = root.querySelector('[data-carousel-track]');
    const viewport = root.querySelector('[data-carousel-viewport]');

    if (!track || !viewport) {
        return;
    }

    const slides = Array.from(track.children);

    if (slides.length < 2) {
        return;
    }

    const arrows = root.querySelectorAll('[data-carousel-arrow]');
    const dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));
    const prevArrow = root.querySelector('[data-carousel-arrow="prev"]');
    const nextArrow = root.querySelector('[data-carousel-arrow="next"]');

    const autoplay = root.dataset.carouselAutoplay === '1';
    const speedSeconds = parseInt(root.dataset.carouselSpeed, 10);
    const intervalMs =
        (Number.isFinite(speedSeconds) && speedSeconds >= 3
            ? speedSeconds
            : DEFAULT_SPEED_SECONDS) * 1000;

    const reduceMotion =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let index = 0;
    let timer = null;

    // Furthest slide index we can land on before the track would overscroll
    // past its right edge. Recomputed on resize because slides-per-view is
    // responsive.
    const maxIndex = () => {
        const maxScroll = track.scrollWidth - viewport.clientWidth;
        for (let i = 0; i < slides.length; i += 1) {
            if (slides[i].offsetLeft >= maxScroll) {
                return i;
            }
        }
        return slides.length - 1;
    };

    const render = () => {
        const limit = maxIndex();
        index = Math.max(0, Math.min(index, limit));

        const maxScroll = track.scrollWidth - viewport.clientWidth;
        const offset = Math.min(slides[index].offsetLeft, Math.max(0, maxScroll));
        track.style.transform = `translateX(${-offset}px)`;

        if (prevArrow) {
            prevArrow.disabled = index <= 0;
        }
        if (nextArrow) {
            nextArrow.disabled = index >= limit;
        }

        dots.forEach((dot, i) => {
            const active = i === index;
            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-selected', active ? 'true' : 'false');
            // Dots past the last reachable index can't be landed on.
            dot.disabled = i > limit;
        });
    };

    const goTo = (next) => {
        index = next;
        render();
    };

    const stop = () => {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
    };

    const start = () => {
        if (!autoplay || reduceMotion || timer) {
            return;
        }
        timer = setInterval(() => {
            // Loop back to the start once the end is reached.
            goTo(index >= maxIndex() ? 0 : index + 1);
        }, intervalMs);
    };

    const restart = () => {
        stop();
        start();
    };

    arrows.forEach((arrow) => {
        arrow.addEventListener('click', () => {
            const dir = arrow.dataset.carouselArrow === 'prev' ? -1 : 1;
            goTo(index + dir);
            restart();
        });
    });

    dots.forEach((dot) => {
        dot.addEventListener('click', () => {
            const target = parseInt(dot.dataset.carouselDot, 10);
            if (Number.isFinite(target)) {
                goTo(target);
                restart();
            }
        });
    });

    // Pause while the visitor is interacting; resume when they leave.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    let resizeRaf = null;
    window.addEventListener('resize', () => {
        if (resizeRaf) {
            window.cancelAnimationFrame(resizeRaf);
        }
        resizeRaf = window.requestAnimationFrame(render);
    });

    render();
    start();
}

export default function imageCarousel() {
    document.querySelectorAll('[data-image-carousel]').forEach(buildCarousel);
}
